import { Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { CustomerSearchDTO } from 'src/app/models/dtos/customer/customer-search-dto';
import { CustomerService } from 'src/app/services/customer.service';

/**
 * Seletor de cliente GENÉRICO com busca assíncrona (autocomplete) a partir de 3 caracteres.
 *
 * Diferente de `app-customer-search-select` (que busca só donos de título, via o endpoint
 * de convites), este consulta TODOS os clientes da marina por nome — endpoint
 * `GET /api-core/secure/customer/search`. É o componente a ser reutilizado nas telas
 * gerais (filtros de Boletos, Faturas, Pedidos, etc.).
 *
 * O valor do controle é o `customerId` (number):
 *   <app-customer-search-field [(ngModel)]="filter.customerId"></app-customer-search-field>
 */
@Component({
  selector: 'app-customer-search-field',
  templateUrl: './customer-search-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerSearchFieldComponent),
      multi: true,
    },
  ],
})
export class CustomerSearchFieldComponent implements ControlValueAccessor {
  @Input() placeholder = 'Digite pelo menos 3 caracteres (nome)...';
  @Input() minLength = 3;
  /** Pausa (ms) na digitação antes de disparar a consulta (debounce). */
  @Input() delay = 500;

  /**
   * Emite o cliente completo selecionado (id, name, phone, federalId) — ou null ao limpar.
   * Útil para telas que precisam do objeto (ex.: montar o cliente, filtrar barcos) sem
   * carregar a lista inteira. O [(ngModel)] continua entregando só o customerId.
   */
  @Output() customerSelected = new EventEmitter<CustomerSearchDTO | null>();

  filteredCustomers: { label: string; value: number; dto: CustomerSearchDTO }[] = [];
  // O p-autoComplete grava aqui a string digitada ou a sugestão (objeto) selecionada.
  displayValue: string | { label: string; value: number; dto: CustomerSearchDTO } | null = '';
  disabled = false;

  @ViewChild(AutoComplete) private autoComplete?: AutoComplete;

  private onChange: (value: number) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private customerService: CustomerService) {}

  writeValue(value: number): void {
    // Filtros começam vazios; ao limpar o id externamente, limpamos o texto exibido.
    // Não há resolução reversa id -> nome (sem uso em tela de edição por ora).
    if (value == null) {
      this.displayValue = '';
      this.filteredCustomers = [];
      this.cancelPendingForcedSelection();
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  searchCustomers(event: { query: string }): void {
    const query = event.query;
    if (!query || query.length < this.minLength) {
      this.filteredCustomers = [];
      return;
    }
    this.customerService.searchCustomerByName(query).subscribe({
      next: (data: CustomerSearchDTO[]) => {
        this.filteredCustomers = (data || []).map((c) => ({
          label: c.name,
          value: c.id,
          dto: c,
        }));
      },
      error: () => {
        this.filteredCustomers = [];
      },
    });
  }

  onSelect(event: any): void {
    if (event && event.value !== undefined) {
      this.onChange(event.value);
      this.customerSelected.emit(event.dto ?? null);
      this.onTouched();
    }
  }

  onClear(): void {
    this.displayValue = '';
    this.onChange(null);
    this.customerSelected.emit(null);
    this.onTouched();
  }

  /**
   * EBUG-387: com forceSelection, o p-autoComplete (PrimeNG 16) só seleciona o nome digitado
   * 250 ms depois do change (setTimeout em onInputChange). Quem sai do campo direto para o
   * Pesquisar, com clique ou Enter, submete antes disso, com o customerId ainda nulo.
   * Chamado no blur (que vem depois do change) e no keydown.enter (antes do submit): se o texto
   * casa exatamente, com trim, com o label de uma sugestão, a mesma regra do forceSelection,
   * o id vai na hora. A seleção do PrimeNG chega depois com o mesmo id. O clique em outra
   * sugestão continua valendo: o selectItem cancela o timer e passa pelo onSelect.
   */
  confirmTypedCustomer(event: Event): void {
    const typed = ((event?.target as HTMLInputElement)?.value ?? '').trim();
    if (!typed) {
      return;
    }
    // O cliente já selecionado tem prioridade: com homônimos, a busca na lista acharia o primeiro.
    const selected = this.displayValue && typeof this.displayValue === 'object' ? this.displayValue : null;
    const match = selected && selected.label?.trim() === typed
      ? selected
      : this.filteredCustomers.find((c) => c.label?.trim() === typed);
    if (match) {
      this.onChange(match.value);
      this.customerSelected.emit(match.dto ?? null);
      this.onTouched();
    }
  }

  /**
   * EBUG-387: digitar o nome exato e clicar direto no LIMPAR deixava pendente o setTimeout de
   * 250 ms do forceSelection, que selecionava o cliente de novo depois da tela zerar o filtro.
   * O texto também é apagado aqui: ainda não houve seleção, então o displayValue já era '' e o
   * ngModel interno não repassaria a limpeza ao campo.
   */
  private cancelPendingForcedSelection(): void {
    const autoComplete = this.autoComplete;
    if (autoComplete?.forceSelectionUpdateModelTimeout) {
      clearTimeout(autoComplete.forceSelectionUpdateModelTimeout);
      autoComplete.forceSelectionUpdateModelTimeout = null;
      autoComplete.writeValue('');
    }
  }
}
